// HistoryUpgradeBanner — visas ovanför ChatSidebar när user närmar sig
// maxConversations-limiten (≥80%). Använder den delade UpgradeBadge:n
// så CTA:n följer brand-konsistens (#37).

import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UpgradeBadge } from '@/components/upgrade/UpgradeBadge';
import type { PlanTier } from '@/lib/planTiers';

export interface HistoryUpgradeBannerProps {
  currentCount: number;
  limit: number;
  currentTier: string;
  /** Vilken tier user behöver för fler chattar. Saknad → ingen CTA. */
  requiredTier?: PlanTier;
  onDismiss?: () => void;
}

export function HistoryUpgradeBanner({
  currentCount, limit, currentTier, requiredTier, onDismiss,
}: HistoryUpgradeBannerProps) {
  if (limit <= 0) return null; // unlimited
  const ratio = currentCount / limit;
  if (ratio < 0.8) return null; // inte nära än

  const isAtLimit = currentCount >= limit;

  return (
    <div
      className={`px-2 py-2 text-[11px] flex items-start gap-1.5 border-b ${
        isAtLimit
          ? 'bg-red-100 text-red-900 dark:bg-red-950/40 dark:text-red-200'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
      }`}
      data-testid="history-upgrade-banner"
    >
      <div className="flex-1 space-y-1">
        <div>
          <strong>{currentCount}/{limit}</strong> chattar på {currentTier}-planen.
          {isAtLimit
            ? ' Gräns nådd — radera någon, eller uppgradera.'
            : ' Närmar sig gränsen.'}
        </div>
        {requiredTier && <UpgradeBadge tier={requiredTier} />}
      </div>
      {onDismiss && (
        <Button size="icon" variant="ghost" className="h-5 w-5" onClick={onDismiss}>
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}
